import { useState, useEffect } from "react";
import {
  Typography,
  LinearProgress,
  CircularProgress,
  TableRow,
  TableCell,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Autocomplete,
  Tooltip,
  IconButton
} from "@mui/material";
import { getAnalyticsFlattened, getAnalyticsOptions, getAnalyticsOrderTransaction, getAnalyticsTotals } from "../../../API/compliance";
import {
  useUserContext,
} from "../../../contexts/ProviderProvider";
import useNotification from "../../../hooks/useNotification";
import MUIDataTable from "mui-datatables";
import TrueFalseCheckbox from "../../common/TrueFalseCheckbox";
import MultiSelect from "../../common/MultiSelect";
import DateRangePicker from '../../common/DateRangePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CloudDownload } from "@mui/icons-material";

dayjs.extend(utc);
dayjs.extend(timezone);

const format2 = (v) => Number(v).toFixed(2);

const ExpandableRow = (props) => {
  return (
    <div
      style={{
        margin: "10px 10px"
      }}>
      <MUIDataTable
        title={props.title}
        data={props.data}
        columns={props.columns}
        options={props.options}
        components={{ TableToolbar(props) { return null; }, }}
      />
    </div>
  );
};

const BusinessTotalsDashboard = ({ analyticsTotalData }) => {

  const titles = {
    gross_profit: "Net Profit",
    player_profit: "Athlete Profit",
    quantity_sum: "Total Quantity",
    sales_sum: "Total Sales",
    total_order: "Total Orders",
    total_royalties: "Total Royalty",
    school_ytd: "School YTD Rank"
  }

  return (
    <Grid container spacing={1}>
      {Object.entries(analyticsTotalData).map(([key, value]) => {
        if (!value || value === "") return;
        return (
          <Grid item xs={12} sm={4} md={4} lg={2} key={key}>
            <Card>
              <CardContent>
                <Typography
                  variant="h7"
                  gutterBottom
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {titles[key] ? titles[key] : key}
                </Typography>
                <Typography variant="h5">{titles[key] && key === "school_ytd" ? value : format2(value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )
      })}
    </Grid>
  );
};

const AnalyticsDashboardV2 = (props) => {
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingQuery, setLoadingQuery] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [analyticsSalesData, setAnalyticsSalesData] = useState([]);
  const [analyticsTotals, setAnalyticsTotals] = useState([]);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [schools, setSchools] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [schoolInput, setSchoolInput] = useState("");

  const [showByValue, setShowByValue] = useState("Player");
  const [showByInput, setShowByInput] = useState("Player");

  const [skus, setSkus] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [skusInput, setSkusInput] = useState("");

  const [licensingCompanies, setLicensingCompanies] = useState([]);
  const [selectedLicensingCompanies, setSelectedLicensingCompanies] = useState([]);
  const [licensingCompaniesInput, setLicensingCompaniesInput] = useState("");

  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [productTypesInput, setProductTypesInput] = useState("");

  const today = new Date();
  const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth());

  const [startDate, setStartDate] = useState(dayjs.tz(startOfCurrentMonth.toISOString().split('T')[0], 'America/Chicago').startOf('day'));
  const [endDate, setEndDate] = useState(dayjs.tz(today.toISOString().split('T')[0], 'America/Chicago').endOf('day'));

  const [sendQuery, setSendQuery] = useState(0);
  const [filterQuery, setFilterQuery] = useState(0);

  const user = useUserContext();
  const notify = useNotification();

  // load dynamic options lists based on changes in selectors
  useEffect(() => {
    if (!user) { return }
    const analyticsData = {
      analytics_options: {
        schools: selectedSchools,
        sku_name: selectedSkus,
        licensing_company: selectedLicensingCompanies,
        product_type: selectedProductTypes
      }
    }

    const fetch = async () => {
      try {
        const optionsResult = await getAnalyticsOptions(analyticsData, user);
        console.log('result from GC from options: ', optionsResult)

        const analyticsOptionResult = optionsResult.data?.obj?.analytics_options

        if (!analyticsOptionResult) return

        setSkus(analyticsOptionResult?.sku_name)
        setSchools(analyticsOptionResult?.schools)
        setLicensingCompanies(analyticsOptionResult?.licensing_company)
        setProductTypes(analyticsOptionResult?.product_type)

      } catch (error) {
        console.log('error ftching: ', error)
      } finally {
        setLoadingOptions(false)
      }
    }
    fetch();
  }, [user, filterQuery]);

  useEffect(() => {
    if (!user || sendQuery == 0) { return }

    const fetch = async () => {

      try {
        const analyticsData = {
          schools: selectedSchools.length === 0 ? ["All"] : selectedSchools,
          sku_name: selectedSkus.length === 0 ? ["All"] : selectedSkus,
          licensing_company: selectedLicensingCompanies.length === 0 ? ["All"] : selectedLicensingCompanies,
          product_type: selectedProductTypes.length === 0 ? ["All"] : selectedProductTypes,
          groupBy: showByValue
        }
        setAnalyticsSalesData([])
        const formatStartDate = startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss");
        const formatEndDate = endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss");

        const [analyticsTotalOT, analyticsAthleteOT] = await Promise.all([
          getAnalyticsTotals(user, analyticsData, formatStartDate, formatEndDate, props.view),
          getAnalyticsOrderTransaction(user, analyticsData, formatStartDate, formatEndDate, props.view)
        ]);

        console.log('response from backend for Analytics Total - v2 for: ', showByValue, analyticsTotalOT)
        console.log('response from backend for Analytics Athlete - v2 for: ', showByValue, analyticsAthleteOT)

        setAnalyticsSalesData(analyticsAthleteOT.data.obj);
        setAnalyticsTotals(analyticsTotalOT.data.obj[0]);
      } catch (error) {
        notify("Failed to get Analytics data");
        console.error("Error fetching data: ", error);
      } finally {
        setLoadingQuery(false);
      }

    }

    fetch();

  }, [user, sendQuery, startDate, endDate]);


  const handleFilterQueryClick = () => {
    console.log('Filter query options')
    setLoadingOptions(true)
    setFilterQuery(filterQuery + 1);
  };

  const handleSendQueryClick = () => {
    console.log('Send query as written')
    setLoadingQuery(true)
    setSendQuery(sendQuery + 1);
  };

  const handleAnalyticsDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const analyticsData = {
        schools: selectedSchools.length === 0 ? ["All"] : selectedSchools,
        sku_name: selectedSkus.length === 0 ? ["All"] : selectedSkus,
        licensing_company: selectedLicensingCompanies.length === 0 ? ["All"] : selectedLicensingCompanies,
        product_type: selectedProductTypes.length === 0 ? ["All"] : selectedProductTypes,
        groupBy: showByValue
      }

      const formatStartDate = startDate.startOf("day").format("YYYY-MM-DD HH:mm:ss");
      const formateEndDate = endDate.endOf("day").format("YYYY-MM-DD HH:mm:ss");

      const response = await getAnalyticsFlattened(user, analyticsData, formatStartDate, formateEndDate, props.view);

      console.log('response: ', response)

      const blob = response.data;

      let contentDisposition = response.headers['content-disposition']

      let filename;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1].replace(/["']/g, '');
        }
      }

      if (!filename) {
        const fileStartDate = dayjs.tz(startDate, "America/Chicago").format("MM/DD/YYYY")
        const fileEndDate = dayjs.tz(startDate, "America/Chicago").format("MM/DD/YYYY")

        filename = `Athlete_Analytics_Data_${fileStartDate}_to_${fileEndDate}.csv`;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      notify("Failed to download Analytics data");
      console.error(error)
    } finally {
      setIsDownloading(false)
    }
  }

  const CustomDownloadButton = ({ onClick, isDownloading }) => {
    return (
      <Tooltip title={isDownloading ? "Downloading..." : "Download data group by Player"}>
        <span>
          <IconButton
            disabled={isDownloading}
            onClick={onClick}
            style={{ color: isDownloading ? 'gray' : 'inherit' }}
          >
            {isDownloading ? <CircularProgress size={24} /> : <CloudDownload style={{ fill: "text.primary" }} />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const customToolbar = () => {
    return (
      <CustomDownloadButton
        onClick={handleAnalyticsDownload}
        isDownloading={isDownloading}
      />
    );
  };

  // Transaction Fees (0.85%)	University Royalty Fee	Service Fee (4%)	Transfer Fee (2%)	Gross profit 	Player's profit (60%)
  const playerColumns = [
    {
      label: "Athlete", name: "name", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Shopify Collection ID", name: "shopify_collection_id", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: "University", name: "university", options: { display: selectedSchools && (selectedSchools.length > 1 || selectedSchools[0] === "All" && showByValue !== "University"), sortThirdClickReset: true } },
    { label: "Sport", name: "sport", options: { display: false } },
    { label: "State", name: "state", options: { display: false } },
    { label: "Division", name: "division", options: { display: false } },
    { label: "Conference", name: "conference", options: { display: false } },
    { label: "Gender", name: "gender", options: { display: false } },
    { label: "Sub Division", name: "subDivision", options: { display: false } },
    {
      label: "Total Quantity Sold",
      name: "quantity_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Orders",
      name: "order_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Sales",
      name: "sales_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Cost of Goods",
      name: "cogs",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transaction Fees", name: "trans_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "University Royalty Fees", name: "roy_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Service Fees", name: "serv_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transfer Fees", name: "transfer_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Net Profit", name: "net", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Athlete Profit", name: "play_prof", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: 'Line Items', name: 'line_items', options: { display: false, filter: false } }
  ]

  const playerOptions = {
    filter: true,
    // sortOrder: {name:"selected", direction:'desc'},
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: rowsPerPage,
    expandableRows: true,
    onChangeRowsPerPage: (num) => setRowsPerPage(num),
    renderExpandableRow: (rowData, rowMeta, a) => {
      const colSpan = 7;
      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <ExpandableRow
              user={user}
              data={
                analyticsSalesData[rowMeta.dataIndex].player_trans.sort((a, b) => {
                  return new Date(b.date) - new Date(a.date);
                })
              }
              school={analyticsSalesData[rowMeta.dataIndex].university}
              title={"OrderTransaction"}
              columns={playerExpandableColumns}
              options={playerExpandableOptions}
              notify={notify} />
          </TableCell>
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: loadingQuery ? (
          <CircularProgress />
        ) : (
          "No Records"
        ),
      },
    },
    download: false,
    customToolbar: customToolbar,
  };

  const playerExpandableColumns = [
    {
      label: "Title", name: "title", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value?.replace(props.school, '').replace(/-[^-]*-/, '-')}</div>
        ),
      }
    },
    { label: "Price", name: "price", options: {} },
    { label: "Quantity", name: "quantity", options: {} },
    {
      label: "Date", name: "date", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          new Date(value).toLocaleDateString()
        ),
      }
    },
    {
      name: "royaltyBearing",
      label: "Royalty Bearing",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <TrueFalseCheckbox checked={value !== "" && value !== null && value} disabled />
        ),
      },
    },
  ]

  const playerExpandableOptions = {
    filter: true,
    sortOrder: { name: "date", direction: 'desc' },
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: 10,
    // customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
    //   return ( null );
    // },
  };

  const universityColumns = [
    { label: "University", name: "university", options: { sortThirdClickReset: true } },
    { label: "Rank", name: "rank", options: { sortThirdClickReset: true } },
    { label: "State", name: "state", options: { sortThirdClickReset: true } },
    { label: "Division", name: "division", options: { sortThirdClickReset: true } },
    { label: "Conference", name: "conference", options: { sortThirdClickReset: true } },
    {
      label: "Sub Division", name: "subDivision", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Quantity Sold",
      name: "quantity_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Orders",
      name: "order_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Sales",
      name: "sales_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "University Royalty Fees", name: "roy_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Net Profit", name: "net", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Athlete Profit", name: "play_prof", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: 'Line Items', name: 'line_items', options: { display: false, filter: false } }
  ]


  const universityOptions = {
    filter: true,
    // sortOrder: {name:"selected", direction:'desc'},
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: rowsPerPage,
    expandableRows: true,
    onChangeRowsPerPage: (num) => setRowsPerPage(num),
    renderExpandableRow: (rowData, rowMeta, a) => {
      const colSpan = 7;

      const modifiedPlayerOption = {
        ...playerOptions,
        renderExpandableRow: (playerRawData, playerRowMeta) => {
          const playerTransData = analyticsSalesData[rowMeta.dataIndex].player_trans[playerRowMeta.dataIndex]?.player_trans || [];
          return (
            <TableRow>
              <TableCell colSpan={colSpan}>
                <ExpandableRow
                  user={user}
                  data={playerTransData.sort((a, b) => new Date(b.date) - new Date(b.date))}
                  school={analyticsSalesData[rowMeta.dataIndex].university}
                  title={"OrderTransaction"}
                  columns={playerExpandableColumns}
                  options={playerExpandableOptions}
                  notify={notify}
                />
              </TableCell>
            </TableRow>
          )
        }
      }
      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <ExpandableRow
              user={user}
              data={analyticsSalesData[rowMeta.dataIndex].player_trans}
              school={analyticsSalesData[rowMeta.dataIndex].university}
              columns={playerColumns}
              options={modifiedPlayerOption}
              notify={notify} />
          </TableCell>
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: loadingQuery ? (
          <CircularProgress />
        ) : (
          "No Records"
        ),
      },
    },
    download: false,
    customToolbar: customToolbar,
  };

  const prodColumns = [
    {
      label: "Product", name: "product_type", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Quantity Sold",
      name: "quantity_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Orders",
      name: "order_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Sales",
      name: "sales_sum",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Cost of Goods",
      name: "cogs",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transaction Fees", name: "trans_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "University Royalty Fees", name: "roy_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Service Fees", name: "serv_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transfer Fees", name: "transfer_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Net Profit", name: "net", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Athlete Profit", name: "play_prof", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: 'Line Items', name: 'line_items', options: { display: false, filter: false } }
  ]

  const prodOptions = {
    filter: true,
    // sortOrder: {name:"selected", direction:'desc'},
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: rowsPerPage,
    expandableRows: true,
    onChangeRowsPerPage: (num) => setRowsPerPage(num),
    renderExpandableRow: (rowData, rowMeta, a) => {
      const colSpan = 7;
      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <ExpandableRow
              user={user}
              data={analyticsSalesData[rowMeta.dataIndex].player_trans}
              columns={prodExpandableColumns}
              options={prodExpandableOptions}
              title={'Order History'}
              notify={notify} />
          </TableCell>
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: loadingQuery ? (
          <CircularProgress />
        ) : (
          "No Records"
        ),
      },
    },
    download: false,
    customToolbar: customToolbar,
  };

  const prodExpandableColumns = [
    {
      label: "Athlete", name: "name", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Shopify Collection ID", name: "shopify_collection_id", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Shopify Product ID", name: "shopify_product_id", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Title", name: "title", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value?.replace(props.school, '').replace(/-[^-]*-/, '-')}</div>
        ),
      }
    },
    { label: "Price", name: "price", options: {} },
    { label: "Quantity", name: "quantity", options: {} },
    { label: "Sport", name: "sport", options: { display: false } },
    { label: "Gender", name: "gender", options: { display: false } },
    {
      label: "Date", name: "date", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          new Date(value).toLocaleDateString()
        ),
      }
    },
    {
      name: "royaltyBearing",
      label: "Royalty Bearing",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <TrueFalseCheckbox checked={value !== "" && value !== null && value} disabled />
        ),
      },
    },
    {
      label: "Athlete Profit", name: "play_prof", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: 'Line Items', name: 'line_items', options: { display: false, filter: false } }
  ]

  const prodExpandableOptions = {
    filter: true,
    sortOrder: { name: "date", direction: 'desc' },
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: 10,
    // customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
    //   return ( null );
    // },
  };

  const skuColumns = [
    {
      label: "Sku Name", name: "sku_name", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: "University", name: "university", options: { sortThirdClickReset: true } },
    {
      label: "Total Quantity Sold", name: "quantity_sum", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Orders", name: "order_sum", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Sales", name: "sales_sum", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Cost of Goods", name: "cogs", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transaction Fees", name: "trans_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "University Royalty Fees", name: "roy_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Service Fees", name: "serv_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transfer Fees", name: "transfer_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Net Profit", name: "net", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Athlete Profit", name: "play_prof", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
  ]

  const skuOptions = {
    filter: true,
    sortOrder: { name: "University", direction: 'desc' },
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: rowsPerPage,
    expandableRows: true,
    onChangeRowsPerPage: (num) => setRowsPerPage(num),
    renderExpandableRow: (rowData, rowMeta, a) => {
      const colSpan = 7;
      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <ExpandableRow
              user={user}
              data={analyticsSalesData[rowMeta.dataIndex].player_trans}
              columns={skuExpandableColumns}
              options={skuExpandableOptions}
              title={'Order History'}
              notify={notify} />
          </TableCell>
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: loadingQuery ? (
          <CircularProgress />
        ) : (
          "No Records"
        ),
      },
    },
    download: false,
    customToolbar: customToolbar,
  };

  const skuExpandableColumns = [
    {
      label: "Licensor", name: "licensor", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Invoice Id", name: "invoice_id", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Date", name: "date", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          new Date(value).toLocaleDateString()
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Item Description", name: "item_description", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: "Affinity Distribution Category", name: "affinity_product_category", options: {} },
    {
      label: "Affinity UPI", name: "affinity_UPI", options: {
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    { label: "Affinity Distribution Channel", name: "affinity_distribution_chnnel", options: {} },
    { label: "Retailer", name: "retailer", options: {} },
    { label: "Customer", name: "customer", options: {} },
    { label: "Insignia", name: "insignia", options: {} },
    { label: "Shipping Country", name: "shipping_country", options: {} },
    { label: "Shipping Postal Code", name: "shipping_postal_code", options: {} },
    { label: "Number of Units", name: "quantity", options: {} },
    { label: "Price Per Unit", name: "price", options: {} },
    { label: "Gross Sales", name: "Gross", options: {} },
    { label: "Royalties", name: "royalty", options: {} }
  ]

  const skuExpandableOptions = {
    filter: true,
    sortOrder: { name: "Date", direction: 'desc' },
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: 10,
    // customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
    //   return ( null );
    // },
  };

  const sportColumns = [
    {
      label: "Sport", name: "sport", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{value}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Quantity Sold", name: "quantity_sum", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Orders", name: "order_sum", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{value}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Total Sales", name: "sales_sum", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Cost of Goods", name: "cogs", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transaction Fees", name: "trans_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "University Royalty Fees", name: "roy_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Service Fees", name: "serv_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Transfer Fees", name: "transfer_fee", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Net Profit", name: "net", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
    {
      label: "Athlete Profit", name: "play_prof", options: {
        customBodyRender: (value, tableMeta, updateValue) => (
          <div style={{
            textAlign: "center",
          }}>{format2(value)}</div>
        ),
        customHeadLabelRender: (columnMeta) => (
          <div style={{
            whiteSpace: "nowrap",
          }}>{columnMeta.label}</div>
        ),
        sortCompare: (order) => {
          return (a, b) => {
            const val1 = parseFloat(a.data.replace('$', '').replace(',', '').trim());
            const val2 = parseFloat(b.data.replace('$', '').replace(',', '').trim());

            if (isNaN(val1)) return -1;
            if (isNaN(val2)) return 1;

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        filter: false,
        sortThirdClickReset: true
      }
    },
  ]
  const sportOptions = {
    filter: true,
    sortOrder: { name: "Sport", direction: 'desc' },
    selectableRows: "none",
    selectableRowsHeader: false,
    rowsPerPageOptions: [5, 10, 25, 100, 200],
    rowsPerPage: rowsPerPage,
    expandableRows: false,
    onChangeRowsPerPage: (num) => setRowsPerPage(num),
    textLabels: {
      body: {
        noMatch: loadingQuery ? (
          <CircularProgress />
        ) : (
          "No Records"
        ),
      },
    },
    download: false,
    customToolbar: customToolbar,
  }

  const showByOptions = [
    "University",
    "Player",
    "Product Type",
    "Sku Name",
    "Sport"
  ]

  const tableConfigurations = {
    "Player": {
      columns: playerColumns,
      options: playerOptions,
      title: "Player Sales Data"
    },
    "University": {
      columns: universityColumns,
      options: universityOptions,
      title: "University Sales Data"
    },
    "Product Type": {
      columns: prodColumns,
      options: prodOptions,
      title: "Product Type Sales Data"
    },
    "Sku Name": {
      columns: skuColumns,
      options: skuOptions,
      title: "Sku Names Sales Data"
    },
    "Sport": {
      columns: sportColumns,
      options: sportOptions,
      title: "Sport Sales Data"
    }
  };

  return (
    <div style={{ width: "90%", paddingTop: "5vh" }}>
      <Typography variant="h1">Analytics</Typography>
      {(loadingQuery || loadingOptions) && <LinearProgress />}
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', }}>
        {schools?.length > 0 &&
          <MultiSelect
            value={selectedSchools}
            inputValue={schoolInput}
            options={schools}
            onChange={setSelectedSchools}
            onInputChange={setSchoolInput}
            label={"School List"}
            style={{ width: "30%", margin: "10px 10px" }}
            allButton
          />
        }
        {skus?.length >= 1 &&
          <MultiSelect
            value={selectedSkus}
            inputValue={skusInput}
            options={skus}
            onChange={setSelectedSkus}
            onInputChange={setSkusInput}
            label={"Sku"}
            style={{ width: "30%", margin: "10px 10px" }}
            allButton
          />
        }
        {licensingCompanies?.length >= 1 &&
          <MultiSelect
            value={selectedLicensingCompanies}
            inputValue={licensingCompaniesInput}
            options={licensingCompanies}
            onChange={setSelectedLicensingCompanies}
            onInputChange={setLicensingCompaniesInput}
            label={"Licensing Company"}
            style={{ width: "30%", margin: "10px 10px" }}
            allButton
          />
        }
        {productTypes?.length >= 1 &&
          <MultiSelect
            value={selectedProductTypes}
            inputValue={productTypesInput}
            options={productTypes}
            onChange={setSelectedProductTypes}
            onInputChange={setProductTypesInput}
            label={"Type"}
            style={{ width: "30%", margin: "10px 10px" }}
            allButton
          />
        }
      </div>
      <div style={{
        margin: "10px 10px",
        // justifyContent: "center",
        width: "90%",
        // paddingLeft: "10px"
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
      }}>
        <DateRangePicker
          subtitle="(Central Standard Time)"
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <Autocomplete
          value={showByValue}
          onChange={(event, newValue) => {
            setShowByValue(newValue);
          }}
          inputValue={showByInput}
          onInputChange={(event, newInputValue) => {
            setShowByInput(newInputValue);
          }}
          options={showByOptions}
          style={{ width: "20%", margin: "30px 10px", }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={"Show By"}
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
            />
          )}
        />
      </div>
      <div style={{
        margin: "10px",
        justifyContent: "flex-start", // Align items to the left
        width: "100%", // Full width of the parent
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap', // Allow wrapping if buttons don't fit
        gap: '10px', // Space between buttons
      }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilterQueryClick}
          style={{
            margin: "5px", // Space around each button
            flex: '0 1 auto', // Prevent buttons from stretching
            whiteSpace: 'nowrap', // Prevent text from wrapping inside buttons
          }}
          disabled={loadingOptions}
        >
          {loadingOptions ? (
            <>
              Filter Options
              <CircularProgress size={24} color="inherit" />
            </>
          ) : (
            'Filter Options'
          )}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendQueryClick}
          style={{
            margin: "5px", // Space around each button
            flex: '0 1 auto', // Prevent buttons from stretching
            whiteSpace: 'nowrap', // Prevent text from wrapping inside buttons
          }}
          disabled={loadingQuery}
        >
          {loadingQuery ? (
            <>
              Send Query
              <CircularProgress size={24} color="inherit" />
            </>
          ) : (
            'Send Query'
          )}
        </Button>
      </div>
      {analyticsSalesData &&
        <div
          style={{
            margin: "20px 10px"
          }}>
          {showByValue ?
            <MUIDataTable
              title={tableConfigurations[showByValue].title}
              data={analyticsSalesData}
              columns={tableConfigurations[showByValue].columns}
              options={tableConfigurations[showByValue].options}
            />
            :
            <Typography variant="h6">Please select a Show By Value</Typography>
          }
        </div>
      }
      {analyticsTotals &&
        <div
          style={{
            margin: "10px 10px",
          }}>
          <BusinessTotalsDashboard
            analyticsTotalData={analyticsTotals}
          />
        </div>}
    </div>
  )
};

export default AnalyticsDashboardV2